import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Check, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getProduct, getProducts } from '../services/api';
import { toast } from '../hooks/use-toast';

const ProductDetail = ({ onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    const productData = await getProduct(id);
    if (productData) {
      setProduct(productData);
      setSelectedSize(productData.sizes[0]);
      setSelectedColor(productData.colors[0]);
      
      // Fetch related products
      const allProducts = await getProducts({ category: productData.category });
      const related = allProducts.filter(p => p.id !== productData.id).slice(0, 4);
      setRelatedProducts(related);
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Product not found</h2>
        <Link to="/products">
          <Button>Back to Products</Button>
        </Link>
      </div>
    );
  }

  const handleAddToCart = () => {
    onAddToCart({ ...product, selectedSize, selectedColor, quantity });
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-slate-600 mb-8">
        <Link to="/" className="hover:text-slate-900">Home</Link>
        <span>/</span>
        <Link to="/products" className="hover:text-slate-900">Products</Link>
        <span>/</span>
        <span className="text-slate-900">{product.name}</span>
      </div>

      {/* Product Details */}
      <div className="grid md:grid-cols-2 gap-12 mb-20">
        {/* Product Image */}
        <div className="relative">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-[600px] object-cover rounded-2xl shadow-lg"
          />
          {product.badge && (
            <Badge className="absolute top-6 right-6 bg-slate-900 text-white px-4 py-2 text-sm">
              {product.badge}
            </Badge>
          )}
        </div>

        {/* Product Info */}
        <div>
          <div className="mb-4">
            <span className="text-sm text-slate-600">{product.category} / {product.subcategory}</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{product.name}</h1>
          
          {/* Rating */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < Math.floor(product.rating)
                      ? 'text-yellow-500 fill-current'
                      : 'text-slate-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-slate-600">{product.rating} ({product.reviews} reviews)</span>
          </div>

          {/* Price */}
          <div className="mb-8">
            <span className="text-4xl font-light text-neutral-900">£{product.price}</span>
          </div>

          {/* Description */}
          <p className="text-slate-600 mb-8">{product.description}</p>

          {/* Size Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-900 mb-3">Size</label>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`px-6 py-3 border-2 rounded-lg font-medium transition-all ${
                    selectedSize === size
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Color Selection */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-900 mb-3">Color</label>
            <div className="flex flex-wrap gap-2">
              {product.colors.map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={`px-6 py-3 border-2 rounded-lg font-medium transition-all ${
                    selectedColor === color
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-900 mb-3">Quantity</label>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 border-2 border-slate-200 rounded-lg hover:border-slate-400 transition-colors"
              >
                -
              </button>
              <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 border-2 border-slate-200 rounded-lg hover:border-slate-400 transition-colors"
              >
                +
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              onClick={handleAddToCart}
              size="lg"
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white h-14 text-lg"
            >
              <ShoppingCart className="mr-2 w-5 h-5" />
              Add to Cart
            </Button>
            <Button size="lg" variant="outline" className="h-14">
              <Heart className="w-5 h-5" />
            </Button>
          </div>

          {/* Product Features */}
          <div className="mt-8 space-y-3">
            <div className="flex items-center space-x-3 text-slate-600">
              <Check className="w-5 h-5 text-green-600" />
              <span>Perfect for small & medium breeds</span>
            </div>
            <div className="flex items-center space-x-3 text-slate-600">
              <Check className="w-5 h-5 text-green-600" />
              <span>Free shipping on orders over $69</span>
            </div>
            <div className="flex items-center space-x-3 text-slate-600">
              <Check className="w-5 h-5 text-green-600" />
              <span>30-day return policy</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="reviews" className="mb-20">
        <TabsList className="w-full justify-start border-b">
          <TabsTrigger value="reviews">Customer Reviews</TabsTrigger>
          <TabsTrigger value="shipping">Shipping Info</TabsTrigger>
          <TabsTrigger value="care">Care Instructions</TabsTrigger>
        </TabsList>
        <TabsContent value="reviews" className="py-8">
          <div className="space-y-6">
            <Card className="border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-900">Sarah M.</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                      ))}
                    </div>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Verified Purchase
                  </Badge>
                </div>
                <p className="text-slate-600">Absolutely love this! Perfect for my small breed. Freddy would approve!</p>
                <p className="text-sm text-slate-400 mt-3">2025-06-15</p>
              </CardContent>
            </Card>
            <Card className="border">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-900">Mike R.</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                      ))}
                    </div>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Verified Purchase
                  </Badge>
                </div>
                <p className="text-slate-600">Great quality and fits perfectly. My pup gets so many compliments!</p>
                <p className="text-sm text-slate-400 mt-3">2025-06-10</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="shipping" className="py-8">
          <div className="prose max-w-none">
            <p className="text-slate-600 mb-4">We offer free standard shipping on all orders over $69.</p>
            <ul className="text-slate-600 space-y-2">
              <li>Standard Shipping (5-7 business days): Free on orders $69+</li>
              <li>Express Shipping (2-3 business days): $14.99</li>
              <li>Next Day Delivery: $24.99</li>
            </ul>
          </div>
        </TabsContent>
        <TabsContent value="care" className="py-8">
          <div className="prose max-w-none">
            <p className="text-slate-600 mb-4">To keep your pet's items looking their best:</p>
            <ul className="text-slate-600 space-y-2">
              <li>Machine wash cold on gentle cycle</li>
              <li>Air dry or tumble dry low</li>
              <li>Do not bleach or iron</li>
              <li>Spot clean jewelry and accessories</li>
            </ul>
          </div>
        </TabsContent>
      </Tabs>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-8">You Might Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {relatedProducts.map((relatedProduct) => (
              <Card key={relatedProduct.id} className="group hover:shadow-xl transition-all border-none">
                <Link to={`/product/${relatedProduct.id}`}>
                  <div className="relative overflow-hidden rounded-t-lg">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </Link>
                <CardContent className="p-6">
                  <Link to={`/product/${relatedProduct.id}`}>
                    <h3 className="font-semibold text-slate-900 mb-2 hover:text-slate-700">
                      {relatedProduct.name}
                    </h3>
                  </Link>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-light text-neutral-900">£{relatedProduct.price}</span>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-neutral-600">{relatedProduct.rating}</span>
                      </div>
                    </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
